import type { GarageInput, GarageView } from '@chukua/common';
import { Button } from 'antd';
import { Plus, ShieldAlert, Wrench } from 'lucide-react';
import { Navigate } from 'react-router';
import { TableSkeleton } from '../../../../../shared/components/TableSkeleton';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { buildDirectoryColumns } from '../../../components/build-directory-columns';
import { DirectoryTable } from '../../../components/DirectoryTable';
import { useCanManageService } from '../../../hooks/use-can-manage-service';
import { useDirectoryDrawer } from '../../../hooks/use-directory-drawer';
import { useDirectoryTags } from '../../../hooks/use-directory-tags';
import { GarageFormDrawer } from '../components/GarageFormDrawer';
import { useGarages } from '../hooks/use-garages';

/** The directory of garages (gitaumoses4/chukua#173): where work can be sent, their service types and certifications. Deactivated, never deleted. */
export function GaragesPage() {
  const catalogue = useDirectoryTags('service_type');
  const { state, list, isRefreshing, saveError, saving, resetSaveError, create, update, deactivate, reactivate } = useGarages(catalogue.refresh);
  const canManage = useCanManageService();
  const drawer = useDirectoryDrawer<GarageView>(resetSaveError);

  if (state.status === 'forbidden') return <Navigate to="/" replace />;

  const actions = canManage ? { onEdit: drawer.openDrawer, onDeactivate: deactivate, onReactivate: reactivate } : undefined;
  const newButton = canManage && (
    <Button type="primary" icon={<Plus className="size-4" />} onClick={() => drawer.openDrawer()}>
      New garage
    </Button>
  );

  return (
    <div className="flex flex-col gap-4">
      {newButton && <div className="flex justify-end">{newButton}</div>}
      {state.status === 'error' && (
        <EmptyState icon={ShieldAlert} title="Couldn't load garages" description="Something went wrong loading the directory. Try reloading the page." />
      )}
      {state.status === 'loading' && <TableSkeleton label="Loading garages" withSearch />}
      {state.status === 'loaded' && (
        <DirectoryTable
          page={state.page}
          columns={buildDirectoryColumns({
            list,
            place: { title: 'Zone', sortKey: 'place', value: (row: GarageView) => row.zoneName ?? row.zoneCode ?? 'No zone yet' },
            tags: { title: 'Service types', filterKey: 'serviceType', catalogue: catalogue.tags, values: (row: GarageView) => row.serviceTypes },
            actions,
          })}
          list={list}
          isRefreshing={isRefreshing}
          newButton={newButton}
          emptyIcon={Wrench}
          emptyTitle="No garages yet"
          emptyDescription="Add a garage work can be sent to, with its service types and certifications."
          searchPlaceholder="Search name or zone"
          noMatchTitle="No matching garages"
        />
      )}
      <GarageFormDrawer
        open={drawer.open}
        editing={drawer.editing}
        catalogue={catalogue.tags}
        error={saveError}
        submitting={saving}
        onSubmit={(values) => (drawer.editing ? update(drawer.editing.id, values as GarageInput) : create(values as GarageInput))}
        onClose={drawer.close}
      />
    </div>
  );
}
